import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clock, ShieldCheck, ListOrdered, Copy, Check, Zap } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { pick } from "@/data/mock";

const HORARIOS = ["09:20", "12:45", "15:10", "18:30", "20:15", "22:40", "23:55"];
const SEQ = ["10 → 25 → 40", "5 → 15 → 30", "8 → 22 → 45", "12 → 28 → 50", "6 → 18 → 36"];
const rnd = (a, b) => Math.round(Math.random() * (b - a) + a);

export const SignalModal = ({ game, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [signal] = useState(() => ({
    time: pick(HORARIOS),
    sequence: pick(SEQ),
    confidence: rnd(82, 98),
  }));

  if (!game) return null;

  const copyText = `SINAL — ${game.name}\nProvedora: ${game.provider}\nStatus: Online\nMelhor horário: ${signal.time}\nSequência sugerida: ${signal.sequence}\nConfiança: ${signal.confidence}%\n\nUse com responsabilidade. Não garante resultados.`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(copyText);
    } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[90] flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="signal-modal"
      >
        <div
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        />
        <motion.div
          initial={{ scale: 0.92, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.92, y: 20, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="relative glass-gold w-full max-w-md overflow-hidden"
        >
          <div className="absolute -top-16 -right-16 w-48 h-48 radial-gold blur-2xl" />

          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full flex items-center justify-center bg-white/5 border border-white/10 text-white/70 hover:text-white transition-colors"
            aria-label="Fechar"
            data-testid="signal-modal-close"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="relative p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-20 rounded-xl overflow-hidden border border-[#F5C542]/30 shrink-0">
                <img src={imgUrl(game.image)} alt={game.name} className="w-full h-full object-cover" />
              </div>
              <div>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-[#00FF41] bg-[#00FF41]/10 border border-[#00FF41]/30 px-2.5 py-1 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00FF41]" /> Online
                </span>
                <div className="font-display font-bold text-white text-xl mt-2 leading-tight">
                  {game.name}
                </div>
                <div className="text-[#F5C542] text-sm">{game.provider}</div>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <Row icon={Clock} label="Melhor horário" value={signal.time} />
              <Row icon={ListOrdered} label="Sequência sugerida" value={signal.sequence} />
              <Row icon={ShieldCheck} label="Nível de confiança" value={`${signal.confidence}%`} />
            </div>

            <button
              onClick={copy}
              className="mt-6 w-full btn-gold font-display font-bold py-3.5 rounded-full flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform"
              data-testid="signal-copy-button"
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              {copied ? "Sinal copiado!" : "Copiar Sinal"}
            </button>

            <div className="mt-4 flex items-start gap-2 text-[11px] text-white/45 leading-relaxed">
              <Zap className="w-3.5 h-3.5 text-[#FFD84D] shrink-0 mt-0.5" />
              Use com responsabilidade. Não representa
              garantia de resultados nem retorno financeiro.
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-center justify-between p-3.5 rounded-xl bg-white/[0.03] border border-white/[0.06]">
    <span className="flex items-center gap-2.5 text-sm text-white/55">
      <Icon className="w-4 h-4 text-[#F5C542]" />
      {label}
    </span>
    <span className="font-display font-bold text-white">{value}</span>
  </div>
);
